// services/blanka.js — логиката на „Работна бланка" (табличен изглед в Чек лист).
//
// ЧИСТИ функции: подават им се фирми/колони/клетки, няма достъп до базата.
// Матрицата е „фирма × задача" по месеци. Тук се решава кои колони се
// показват, кои клетки важат за коя фирма, как изглежда всеки статус и как
// се разчитат кодовете от стария Excel при импорт.

// Тримесечното ДДС се подава в МЕСЕЦА СЛЕД тримесечието
// (до 30 апр / 31 юли / 31 окт / 31 яну) — законово, не се пипа.
const QUARTER_MONTHS = [1, 4, 7, 10];

// чл.55 се ПОКАЗВА в ПОСЛЕДНИЯ месец на тримесечието (март/юни/септ/дек) —
// решение на Макс: бланката се работи с месец назад, затова задачата стои
// при месеца, за който се отнася (законовият срок е края на месеца СЛЕД
// тримесечието, но това е нарочно различно от показването).
const CHL55_MONTHS = [3, 6, 9, 12];

// Вградените задачи по таб (id-тата се пазят в blanka_entries.task).
const BUILTIN = {
  monthly: [
    { key: 'd1d6', label: 'Д1 / Д6' },
    { key: 'dds', label: 'ДДС' },
    { key: 'chl55', label: 'чл. 55' },
  ],
  annual: [
    { key: 'gdd', label: 'ГДД' },
    // Две отделни годишни справки по чл. 73 (ал. 1 и ал. 6) — в старата
    // бланка са два стълба; държим ги като две колони, за да не се губят данни.
    { key: 'chl73_1', label: 'чл. 73, ал. 1' },
    { key: 'chl73_6', label: 'чл. 73, ал. 6' },
    { key: 'advance', label: 'Авансов данък' },
  ],
};
const BUILTIN_KEYS = new Set([...BUILTIN.monthly, ...BUILTIN.annual].map((c) => c.key));

// Жизненият цикъл на статуса + визуалната карта (цвят и отметка).
const STATUS = {
  pending: { label: 'Чака', color: 'gray', mark: '' },
  submitted: { label: 'Подадено', color: 'amber', mark: '✓' },
  verified: { label: 'Проверено', color: 'green', mark: '✓✓' },
  notdue: { label: 'Не подлежи', color: 'gray', mark: '—' },
  notsubmitted: { label: 'Не подадено', color: 'red', mark: '✗' },
};
const STATUS_KEYS = Object.keys(STATUS);

function statusInfo(status) { return STATUS[status] || STATUS.pending; }
function isValidStatus(s) { return STATUS_KEYS.includes(s); }
function isQuarterMonth(month) { return QUARTER_MONTHS.includes(Number(month)); }
function isChl55Month(month) { return CHL55_MONTHS.includes(Number(month)); }

// ---- Колони на матрицата (за избрания таб и месец) ------------------------

// Подрежда и филтрира конфигурационните редове (blanka_columns) за таб.
function configFor(tab, columnsConfig) {
  return (Array.isArray(columnsConfig) ? columnsConfig : [])
    .filter((c) => c && c.tab === tab)
    .slice()
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
}

// Кои колони се виждат за таб+месец. НЕ зависи от фирмата (матрицата е
// таблица с еднакви колони); приложимостта по фирма е cellApplies().
// Вградените могат да имат override (label/order/active/скриване) в config;
// динамичните идват само от config.
function visibleColumns(tab, month, columnsConfig) {
  const cfg = configFor(tab, columnsConfig);
  const byKey = new Map(cfg.filter((c) => c.builtin).map((c) => [c.builtin, c]));

  const cols = [];
  for (const b of (BUILTIN[tab] || [])) {
    const ov = byKey.get(b.key);
    if (ov && ov.active === false) continue;                       // скрита вградена
    if (b.key === 'chl55' && !isChl55Month(month)) continue;       // само март/юни/септ/дек
    cols.push({
      id: b.key, builtin: b.key,
      label: (ov && ov.label) || b.label,
      order: ov ? (Number(ov.order) || 0) : 0,
      applies_to: 'all',
    });
  }
  for (const c of cfg) {
    if (c.builtin) continue;                    // вградените вече са горе
    if (c.active === false) continue;           // скрита динамична — клетките ѝ се пазят, само не се показва
    cols.push({
      id: c.id, builtin: null,
      label: c.label || 'Колона',
      order: Number(c.order) || 0,
      applies_to: c.applies_to === 'flagged' ? 'flagged' : 'all',
      firm_ids: Array.isArray(c.firm_ids) ? c.firm_ids.map(Number) : [],
    });
  }
  // Вградените пазят реда си, освен ако override не зададе order; после динамичните.
  return cols.sort((a, b) => (a.order - b.order));
}

// Дали клетката важи за конкретна фирма (иначе се рисува като неприложима).
function cellApplies(col, firm, month) {
  const f = firm || {};
  if (col.builtin === 'advance') return !!f.has_advance_tax;
  if (col.builtin === 'dds') {
    const vp = f.vat_period || 'none';
    if (vp === 'monthly') return true;
    if (vp === 'quarterly') return isQuarterMonth(month);
    return true; // 'none' — важи, но статусът е принудително notdue
  }
  if (col.applies_to === 'flagged') return (col.firm_ids || []).map(Number).includes(Number(f.id));
  return true;
}

// Принуден статус за клетки, които по правило „не подлежат" (ДДС за нерегистриран).
function forcedStatus(col, firm) {
  if (col.builtin === 'dds' && ((firm && firm.vat_period) || 'none') === 'none') return 'notdue';
  return null;
}

// ---- Обобщение „подадено/проверено X от Y" --------------------------------

// entryOf(firmId, colId) → записа за клетката (или undefined).
// Брои приложимите клетки и колко от тях са готови. „Не подлежи" се брои като
// ГОТОВА: за такава клетка няма какво повече да се направи, значи не бива да
// виси в „остават да се подадат" (искане на Макс — да се смята наравно с
// подадено/проверено). Клетка, която изобщо не е приложима за фирмата (напр.
// авансов данък при фирма без аванси), не е задача и остава извън сметката.
function summarize(firms, columns, month, entryOf) {
  let total = 0, done = 0;
  for (const firm of (firms || [])) {
    for (const col of (columns || [])) {
      if (!cellApplies(col, firm, month)) continue;
      const forced = forcedStatus(col, firm);
      const e = entryOf(firm.id, col.id);
      const st = forced || (e && e.status) || 'pending';
      total++;
      if (st === 'submitted' || st === 'verified' || st === 'notdue') done++;
    }
  }
  return { done, total };
}

// ---- Импорт: разчитане на кодовете от стария Excel ------------------------

function parseAmount(s) {
  const n = parseFloat(String(s).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

// Един код от бланката → { status, amount }.
//   празно      → чака
//   х / x       → не подлежи
//   НЕ          → не подадено
//   да0         → подадено, 0 за внасяне
//   да<число>   → подадено + сума
//   <число>     → подадено + сума
//   да          → подадено (без сума)
//   друго       → чака (безопасно по подразбиране)
function parseImportCode(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (s === '') return { status: 'pending', amount: null };
  const low = s.toLowerCase();
  if (low === 'х' || low === 'x' || low === 'няма' || low === 'nyama') return { status: 'notdue', amount: null };
  if (low === 'не' || low === 'ne') return { status: 'notsubmitted', amount: null };
  if (low === 'да0' || low === 'da0') return { status: 'submitted', amount: 0 };
  // Позволяваме водещ минус: ДДС за възстановяване често е отрицателен.
  const m = low.match(/^(?:да|da)\s*(-?[\d.,]+)$/);
  if (m) return { status: 'submitted', amount: parseAmount(m[1]) };
  if (low === 'да' || low === 'da') return { status: 'submitted', amount: null };
  const num = parseAmount(low);
  if (num != null) return { status: 'submitted', amount: num };
  return { status: 'pending', amount: null };
}

// ---- Импорт от стария Excel: карта на колоните и разчитане на реда --------

const MONTH_NAMES = {
  'януари': 1, 'февруари': 2, 'март': 3, 'април': 4, 'май': 5, 'юни': 6,
  'юли': 7, 'август': 8, 'септември': 9, 'октомври': 10, 'ноември': 11, 'декември': 12,
};

// Заглавие на задача (ред 2 от бланката) → вградено id + дали е годишна.
function importTaskOf(label) {
  const s = String(label || '').trim().toLowerCase();
  if (s.startsWith('д1')) return { task: 'd1d6', annual: false };
  if (s === 'ддс') return { task: 'dds', annual: false };
  if (s.startsWith('чл.55')) return { task: 'chl55', annual: false };
  // Двата чл.73 стълба (ал.6 / ал.1) → две отделни задачи, за да не колидират.
  if (s.startsWith('чл.73')) return { task: /6/.test(s) ? 'chl73_6' : 'chl73_1', annual: true };
  if (s === 'гдд') return { task: 'gdd', annual: true };
  return null;
}

// Свързва счетоводител по ИМЕ със съществуващ потребител — САМО точно
// съвпадение (без разхлабено substring; accountant_user_id е и контрол за
// достъп, затова грешно свързване = теч на права). Двусмислените остават
// несвързани. Клиентите (role 'client') НИКОГА не са счетоводители —
// изключват се преди съпоставянето. Връща { user, ambiguous }.
function matchAccountant(users, name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return { user: null, ambiguous: false };
  const list = (users || []).filter((u) => u && u.role !== 'client');
  // 1) Точно съвпадение на цялото име.
  const exact = list.filter((u) => String(u.name || '').trim().toLowerCase() === n);
  if (exact.length === 1) return { user: exact[0], ambiguous: false };
  if (exact.length > 1) return { user: null, ambiguous: true };
  // 2) Резервно: по ПЪРВО име (уникално). За бланки, в които е записано само
  //    собственото име (МАКСИМ), а потребителят е с пълно („Максим Вергов").
  //    Уникалността пази от объркване — двама с еднакво първо име → несвързан.
  const firstOf = (u) => String(u.name || '').trim().toLowerCase().split(/\s+/)[0];
  const byFirst = list.filter((u) => firstOf(u) === n);
  if (byFirst.length === 1) return { user: byFirst[0], ambiguous: false };
  if (byFirst.length > 1) return { user: null, ambiguous: true };
  return { user: null, ambiguous: false };
}

// От двата заглавни реда (месеци, задачи) → карта colIndex -> {task, month, annual}.
// Месецът се „носи" надясно, докато не се смени. чл.55 остава в месеца, под
// който стои в бланката (последния на тримесечието) — съгласувано с
// показването (CHL55_MONTHS), без изместване.
function importColumnMap(headerMonthRow, headerTaskRow, startCol) {
  const hMonth = headerMonthRow || [];
  const hTask = headerTaskRow || [];
  const from = Number.isInteger(startCol) ? startCol : 5;
  const map = {};
  let curMonth = 0;
  for (let c = from; c < hTask.length; c++) {
    const mName = String(hMonth[c] || '').trim().toLowerCase();
    if (MONTH_NAMES[mName]) curMonth = MONTH_NAMES[mName];
    const t = importTaskOf(hTask[c]);
    if (!t) continue;
    const month = t.annual ? 0 : curMonth;
    map[c] = { task: t.task, month, annual: t.annual };
  }
  return map;
}

// Един ред-фирма → { name, eik, email, accName, signature, vat_period, cells }.
// year: коя година е бланката; декемврийските клетки отиват в year-1.
function importFirmRow(row, colMap, year) {
  const r = row || [];
  const cells = [];
  let hasDdsActivity = false;
  for (const c of Object.keys(colMap)) {
    const m = colMap[c];
    const raw = r[c];
    if (String(raw == null ? '' : raw).trim() === '') continue; // празно = чака → без запис
    const parsed = parseImportCode(raw);
    const e = { year: m.annual ? year : (m.month === 12 ? year - 1 : year), month: m.month, task: m.task, status: parsed.status };
    if (m.task === 'dds') {
      e.amount = parsed.amount;
      if (parsed.status === 'submitted' || parsed.status === 'notsubmitted') hasDdsActivity = true;
    } else if (m.task === 'd1d6' && parsed.amount != null) {
      e.note = parsed.amount + ' лица';
    }
    cells.push(e);
  }
  return {
    name: String(r[0] || '').trim(),
    eik: String(r[4] || '').trim(),
    email: String(r[1] || '').trim(),
    accName: String(r[2] || '').trim(),
    signature: String(r[3] || '').trim().toUpperCase() === 'ДВ' ? 'ДВ' : 'БВ',
    vat_period: hasDdsActivity ? 'monthly' : 'none',
    cells,
  };
}

// Текстът на CSV → редове (масив от масиви). Разделител „;".
function parseCsvText(text) {
  return String(text || '').split(/\r?\n/).map((r) => r.split(';'));
}

// Пълният план за импорт: редовете + годината + потребителите (за свързване
// на счетоводителя по точно име) → { columns, firms:[{...,accountant_user_id}],
// unmatched, ambiguous }. Общо за скрипта и за импорта през браузъра.
function buildImportPlan(rows, year, users) {
  const colMap = importColumnMap(rows[1], rows[2]);
  const firms = [];
  const unmatched = new Set(), ambiguous = new Set();
  for (let r = 3; r < rows.length; r++) {
    const parsed = importFirmRow(rows[r], colMap, year);
    if (!parsed.name) continue;
    const { user, ambiguous: amb } = matchAccountant(users || [], parsed.accName);
    if (parsed.accName && !user) (amb ? ambiguous : unmatched).add(parsed.accName);
    firms.push(Object.assign(parsed, { accountant_user_id: user ? user.id : null }));
  }
  return { columns: Object.keys(colMap).length, firms, unmatched: [...unmatched], ambiguous: [...ambiguous] };
}

module.exports = {
  QUARTER_MONTHS, CHL55_MONTHS, BUILTIN, BUILTIN_KEYS, STATUS, STATUS_KEYS, MONTH_NAMES,
  statusInfo, isValidStatus, isQuarterMonth, isChl55Month,
  visibleColumns, cellApplies, forcedStatus, summarize,
  parseImportCode, parseAmount,
  importTaskOf, importColumnMap, importFirmRow, matchAccountant,
  parseCsvText, buildImportPlan,
};
